import { useEffect, useMemo, useState } from 'react';
import { decryptQuestionBody, type QuestionBody, type StateView } from '@quiz/shared';
import type { PlayerSession } from '../lib/session.js';
import { api, ApiError } from '../lib/api.js';
import { useCountdown } from '../lib/usePoll.js';
import { Button, Card, Countdown, Banner } from '../components/ui.js';
import { useMediaUrl } from './useMedia.js';
import { markReady } from '../lib/readySignal.js';
import { isCached } from '../lib/mediaCache.js';
import { QuestionMedia } from './QuestionMedia.js';

/**
 * Decrypt the current question's body using the key that arrived in the state
 * poll. Until the host opens the question there is no key, and the phone is
 * holding a blob it cannot read.
 */
function useDecrypted(session: PlayerSession, state: StateView): QuestionBody | null | 'error' {
  const [body, setBody] = useState<QuestionBody | null | 'error'>(null);
  const questionId = state.question?.id;
  const key = state.question?.key;
  const roundId = state.round?.id;

  const sealed = useMemo(() => {
    if (!questionId || !roundId) return null;
    // Find within the round: the same question may legitimately appear in two
    // rounds, and they are separate ciphertexts.
    const round = session.quiz.rounds.find((r) => r.id === roundId);
    return round?.questions.find((q) => q.id === questionId) ?? null;
  }, [session.quiz, questionId, roundId]);

  useEffect(() => {
    let cancelled = false;
    if (!sealed || !key) {
      setBody(null);
      return;
    }
    decryptQuestionBody(key, sealed)
      .then((b) => !cancelled && setBody(b))
      .catch(() => !cancelled && setBody('error'));
    return () => {
      cancelled = true;
    };
  }, [sealed, key]);

  return body;
}

export function QuestionScreen({
  session,
  state,
  offsetMs,
  onAnswered,
}: {
  session: PlayerSession;
  state: StateView;
  offsetMs: number;
  onAnswered: () => void;
}) {
  const body = useDecrypted(session, state);
  const [selected, setSelected] = useState<string>('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const question = state.question;
  const msLeft = useCountdown(question?.endsAt ?? null, offsetMs);

  // A new question clears the previous one's selection.
  useEffect(() => {
    setSelected('');
    setText('');
    setError(null);
  }, [question?.id]);

  const sealed = session.quiz.rounds
    .find((r) => r.id === state.round?.id)
    ?.questions.find((q) => q.id === question?.id);
  const isFreeText = sealed?.format === 'FREE_TEXT';

  // The bytes have been on this phone since the door; the key arriving is what
  // makes them readable. Nothing crosses the network here unless this handset
  // joined too late to have prefetched.
  // In HOST mode the clip plays over the venue's speaker and was never sent to
  // this phone. Asking for it would 404 on a handset that legitimately has no
  // business holding it.
  const ownMedia =
    sealed?.media && !(state.audioMode === 'HOST' && sealed.media.kind === 'AUDIO')
      ? sealed.media
      : undefined;

  const media = useMediaUrl(
    session.eventId,
    question?.id,
    ownMedia,
    state.question?.key,
    session.playerToken,
  );

  /**
   * Tell the server this phone can actually show the question.
   *
   * "Ready" means the BYTES are on the handset -- not that they have been
   * decrypted. The key does not arrive until the host opens the question, so a
   * signal that waited for decryption could never fire while the question was
   * staged, which is the only moment the host is asking. That was the first
   * version of this, and it left the tally reading 0/1 forever.
   *
   * The download is the slow part. Unsealing two megabytes is milliseconds.
   *
   * Above every early return, because this component has three of them. A hook
   * below one runs on some renders and not others, React throws on the changed
   * hook count, and the whole app unmounts to a white screen mid-quiz.
   */
  useEffect(() => {
    const id = question?.id;
    if (!id) return;

    // No file: the sealed body arrived at join, so this phone is as ready as it
    // is ever going to be.
    if (!ownMedia) {
      markReady(id);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = () => {
      void isCached(session.eventId, id).then((have) => {
        if (cancelled) return;
        if (have) {
          markReady(id);
          return;
        }
        // Still downloading. Ask again rather than waiting for a render that
        // may never come -- the prefetch runs outside React and finishing a
        // file changes nothing this component depends on.
        timer = setTimeout(check, 1500);
      });
    };
    check();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [question?.id, ownMedia, session.eventId]);
  const answer = isFreeText ? text.trim() : selected;
  const locked = state.you.hasAnswered;

  async function submit() {
    if (!question || !answer || busy || locked) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/answer', {
        method: 'POST',
        token: session.playerToken,
        // No timestamp, no elapsed time. The server computes the response time
        // from its own clock; there is nothing useful we could send.
        body: { questionId: question.id, answerText: answer },
      });
      onAnswered();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ALREADY_ANSWERED') {
        onAnswered();
      } else {
        setError(err instanceof ApiError ? err.message : 'That did not send. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!question) return null;

  if (body === 'error') {
    return (
      <Banner kind="error">
        This question would not unlock on your phone. Ask the host to re-open it.
      </Banner>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex items-center justify-between text-sm text-muted">
        <span>
          {state.round?.title} &middot; Question {question.indexInRound + 1} of{' '}
          {question.totalInRound}
        </span>
        <span className="rounded-full bg-ink-2 px-2.5 py-1 text-xs font-semibold text-flag">
          {question.pointsValue} pts
        </span>
      </div>

      {msLeft !== null && <Countdown msLeft={msLeft} totalMs={question.durationMs} />}

      <Card className="flex-1">
        {body === null ? (
          /* Staged but not open: this phone holds the question as ciphertext
             and the key has not arrived. That can last a minute while the host
             reads it aloud, so say what is happening rather than imply
             something is stuck. */
          <p className="text-muted">
            {state.question?.key
              ? 'Unlocking…'
              : 'Get ready — the host is about to open this one.'}
          </p>
        ) : (
          <>
            <h1 className="text-2xl font-semibold leading-snug">{body.prompt}</h1>

            {sealed?.media && (
              <QuestionMedia media={sealed.media} url={media.url} state={media.state} />
            )}

            {locked ? (
              <div className="mt-6 rounded-xl bg-go/15 p-4 text-center">
                <p className="text-lg font-semibold text-go">Answer locked in</p>
                <p className="mt-1 text-sm text-muted">
                  Waiting for the host. You will see how you did in a moment.
                </p>
              </div>
            ) : isFreeText ? (
              <input
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 60))}
                onKeyDown={(e) => e.key === 'Enter' && void submit()}
                autoFocus
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="send"
                placeholder="Type your answer"
                className="mt-6 w-full rounded-xl bg-ink px-4 py-4 text-xl outline-none
                  ring-1 ring-ink-3 focus:ring-2 focus:ring-sky placeholder:text-ink-3"
              />
            ) : (
              <div className="mt-6 space-y-3">
                {body.options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setSelected(option)}
                    aria-pressed={selected === option}
                    className={`min-h-14 w-full rounded-xl px-4 py-3 text-left text-lg font-medium
                      transition active:scale-[0.99] ${
                        selected === option
                          ? 'bg-sky text-ink ring-2 ring-sky'
                          : 'bg-ink ring-1 ring-ink-3 hover:ring-muted'
                      }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      {error && <Banner kind="error">{error}</Banner>}

      {/* Tapping an option only selects it. Submitting is a separate, deliberate
          act -- a fat thumb should not cost somebody the round. */}
      {!locked && (
        <Button variant="go" onClick={() => void submit()} disabled={!answer || busy}>
          {busy ? 'Sending…' : 'Confirm answer'}
        </Button>
      )}

      <div className="flex justify-between text-sm text-muted">
        <span>
          Score {state.you.score} &middot; {ordinal(state.you.rank)}
        </span>
        <span>{state.answeredCount ?? 0} answered</span>
      </div>
    </div>
  );
}

function ordinal(n: number): string {
  if (n <= 0) return '—';
  const teens = n % 100;
  if (teens >= 11 && teens <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
}
